import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Spinner,
    Alert,
    AlertTitle,
    Heading,
    Button,
    useToast,
    Flex,
    HStack
} from "@chakra-ui/react";
import { FaPlus, FaLandmark } from "react-icons/fa";
import CreateTicketModal from '../components/tickets/CreateTicketModal';
import ImagePreviewModal from '../components/tickets/ImagePreviewModal';
import TicketStatusTabs from '../components/tickets/single/TicketStatusTabs';
import StatusChangeModal from '../components/tickets/StatusChangeModal';
import useTickets from "../hooks/useTickets";
import { useNavigate } from "react-router-dom";
import { ticketStatuses } from "../constants/ticketStatusConstants";

const Tickets = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { tickets, fetchTickets, loading, error, createTicket, updateTicketStatus, getShareableLink } = useTickets();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);

    const [selectedTicket, setSelectedTicket] = useState(null);
    const [newStatus, setNewStatus] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    const confirmStatusChange = useCallback((ticket, status) => {
        if (!ticket) return;
        setSelectedTicket(ticket);
        setNewStatus(status);
        setIsStatusModalOpen(true);
    }, []);

    useEffect(() => {
        fetchTickets();
    }, []);

    const handleStatusChange = async () => {
        if (!selectedTicket || newStatus === null) return;
        setIsUpdatingStatus(true);

        try {
            await updateTicketStatus(selectedTicket.id, newStatus);
            setSelectedTicket(null);
            setIsStatusModalOpen(false);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update ticket status.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleCreateTicket = async (ticketData) => {
        await createTicket(ticketData, () => setIsAddModalOpen(false));
    };

    const handleImagePreview = useCallback((imageUrl) => {
        setSelectedImage(imageUrl);
        setIsImagePreviewOpen(true);
    }, []);

    return (
        <Box 
            as="main" 
            pt={{ base: "24px", md: "20px" }} 
            pr={{ base: "0px", md: "30px" }} 
            pb="20px" 
            minH="100vh" 
            pl={{ base: "0px", md: "240px" }}
        >
            <Flex justify="space-between" align="center" mb={5} pl={4} pr={4}>
                <Heading color="brand.700" size="lg">Zadaci</Heading>
                <HStack spacing={3}>
                    <Button
                        border="1px solid"
                        borderColor="brand.600"
                        bg="white"
                        color="brand.700"
                        _hover={{ bg: "brand.50" }}
                        onClick={() => navigate("/sections")}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                    >
                        <FaLandmark />
                    </Button>

                    <Button
                        bg="brand.600"
                        color="white"
                        boxShadow="soft"
                        _hover={{ bg: "brand.700" }}
                        onClick={() => setIsAddModalOpen(true)}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={2}
                    >
                        <FaPlus />
                    </Button>
                </HStack>
            </Flex>

            {error && <Alert status="error"><AlertTitle>{error}</AlertTitle></Alert>}

            <TicketStatusTabs
                tickets={tickets}
                ticketStatuses={ticketStatuses}
                confirmStatusChange={confirmStatusChange}
                handleImagePreview={handleImagePreview}
                getShareableLink={getShareableLink}
            />

            {loading && <Spinner size="xl" color="brand.600" />}
            
            <CreateTicketModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                handleCreateTicket={handleCreateTicket}
            />

            <ImagePreviewModal 
                isOpen={isImagePreviewOpen} 
                onClose={() => setIsImagePreviewOpen(false)} 
                selectedImage={selectedImage} 
            />

            <StatusChangeModal 
                isOpen={isStatusModalOpen} 
                onClose={() => setIsStatusModalOpen(false)} 
                onConfirm={handleStatusChange} 
                selectedTicket={selectedTicket}  
            />
        </Box>
    );
};

export default Tickets;
